import React, { useState } from "react"
import { Paper, Typography, Box, Divider } from "@material-ui/core"
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@material-ui/icons"
import { makeStyles } from "@material-ui/styles"
import { MentionOutput } from ".."
import { Shine } from "./Shine"

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(1),
    cursor: ({ cursor }) => cursor || "inherit",
    border: "2px solid transparent",
    "&:hover": {
      boxShadow: theme.palette.hoverCard.boxShadow,
    },
    "&:focus": {
      boxShadow: theme.palette.hoverCard.boxShadow,
    },
    height: ({ fullHeight }) => (fullHeight ? "100%" : "auto"),
  },
  inner: {
    opacity: ({ isScheduled }) => (isScheduled ? 0.3 : null),
    height: ({ fullHeight }) => (fullHeight ? "100%" : "auto"),
  },
  content: {
    minHeight: ({ contentMinHeight }) => contentMinHeight,
    height: ({ fullHeight }) => (fullHeight ? "100%" : "auto"),
  },
  selected: {
    border: `2px solid ${theme.palette.cards.selected.borderColor}`,
  },
  completed: {
    backgroundColor: theme.palette.cards.completed.backgroundColor,
    border: `2px solid ${theme.palette.cards.completed.borderColor}`,
  },
  overdue: {
    border: `2px solid ${theme.palette.cards.overdue.borderColor}`,
  },
  skipped: {
    backgroundColor: theme.palette.cards.skipped.backgroundColor,
    border: `2px solid ${theme.palette.cards.skipped.borderColor}`,
  },
  title: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: "500",
    color: theme.palette.text.main,
    marginBottom: ({ hasDetail }) => (hasDetail ? theme.spacing(0.5) : 0),
  },
  detail: {
    fontSize: 14,
    lineHeight: "16px",
    color: theme.palette.text.light,
  },
  arrow: {
    color: "black",
  },
  divider: {
    marginLeft: ({ completed, skipped, overdue }) => (completed || skipped || overdue ? 0 : -2),
    marginRight: ({ completed, skipped, overdue }) => (completed || skipped || overdue ? 0 : -2),
    backgroundColor: ({ completed, skipped }) => theme.palette.cards.divide[completed || skipped ? "darker" : "normal"],
  },
  footer: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    color: theme.palette.text.light,
    minHeight: 56,
  },
}))

const ActionCard = ({
  variant = "normal",
  title,
  titleProps = {},
  detail,
  selected = false,
  completed = false,
  overdue,
  skipped,
  stub,
  children,
  innerChildren,
  rightChildren,
  footer,
  cardProps,
  isScheduled,
  fullHeight = false,
  cursor = "pointer",
  className = "",
  classes: userClasses = {},
  contentMinHeight = 65,
  p = 2,
  cy = "ActionCard",
}) => {
  const classes = useStyles({
    isScheduled,
    cursor,
    fullHeight,
    completed,
    skipped,
    overdue,
    contentMinHeight,
    hasDetail: !!detail,
  })
  const [expanded, setExpanded] = useState(false)

  const handleOnClick = (event) => {
    if (isScheduled) return false
    if (variant === "expand") setExpanded(!expanded)
    else if (cardProps && cardProps.onClick) cardProps.onClick(event)
  }

  return (
    <Paper
      elevation={3}
      className={`${classes.root} ${selected ? classes.selected : ""}  ${completed ? classes.completed : ""} ${
        overdue && !completed ? classes.overdue : ""
      } ${skipped && completed ? classes.skipped : ""} ${className}`}
    >
      <Shine go={completed && !skipped} fullHeight={fullHeight}>
        <Box className={classes.inner}>
          <Box {...cardProps} onClick={handleOnClick} data-cy={`${cy}-card`}>
            <Box p={p} className={classes.content} display="flex" flexDirection="column" justifyItems="center">
              <Box display="flex" flexGrow={1} flexDirection="row">
                {stub}
                <Box
                  {...{
                    flexGrow: 1,
                    flexDirection: "column",
                    alignSelf: "center",
                    alignItems: "center",
                    ...titleProps,
                  }}
                >
                  {title && (
                    <Box data-cy={`${cy}-title`}>
                      {typeof title === "string" ? (
                        <Typography variant="h2" className={classes.title}>
                          <MentionOutput content={title} />
                        </Typography>
                      ) : (
                        title
                      )}
                    </Box>
                  )}
                  {detail && <Box className={classes.detail}>{detail}</Box>}
                  {innerChildren}
                </Box>
                {rightChildren}
                {variant === "expand" && children && (
                  <Box display="flex" alignSelf="center" className={classes.arrow}>
                    {expanded && <KeyboardArrowUpIcon />}
                    {!expanded && <KeyboardArrowDownIcon />}
                  </Box>
                )}
                {variant === "navigate" && children && (
                  <Box display="flex" alignSelf="center" className={classes.arrow}>
                    <KeyboardArrowRightIcon />
                  </Box>
                )}
              </Box>
              {(variant !== "expand" || expanded) && children}
            </Box>
            {footer && (
              <>
                <Divider className={classes.divider} />
                <Box
                  display="flex"
                  flexDirection="row"
                  className={userClasses.footer || classes.footer}
                  alignItems="center"
                >
                  {footer}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Shine>
    </Paper>
  )
}

export { ActionCard, useStyles as useActionCardStyles }
